/**
 * NotesDockPanel — slide-in notes panel overlay (notes-5).
 *
 * A dark-themed fixed panel that slides in from the right edge of the screen.
 * It shows all notes for the current scene and lets the learner create new ones
 * without navigating away from the lesson.
 *
 * Props:
 *   isOpen         — boolean, controls visibility
 *   onClose        — () => void
 *   sceneId        — string, current scene/segment ID
 *   tutorialId     — string
 *   domain         — string
 *   notes          — Note[] for this scene (note_type === 'note')
 *   onSaveNote     — async (payload) => void
 *   contextAnchor  — { type: 'timestamp'|'slide', value: number } | null
 *                    Pre-fills the editor with the current video time or slide index
 */

import React, { useState, useEffect, useCallback } from 'react';

function formatAnchor(note) {
  if (note.timestamp_sec != null) {
    const m = Math.floor(note.timestamp_sec / 60);
    const s = String(Math.floor(note.timestamp_sec % 60)).padStart(2, '0');
    return `📍 at ${m}:${s}`;
  }
  if (note.slide_index != null) {
    return `Slide ${note.slide_index + 1}`;
  }
  return null;
}

function NoteCard({ note, onDelete }) {
  const anchor = formatAnchor(note);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 8,
      padding: '10px 12px',
      marginBottom: 8,
    }}>
      {anchor && (
        <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 4 }}>
          {anchor}
        </div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.6, color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>
        {note.text}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, gap: 6 }}>
        {confirmDelete ? (
          <>
            <button
              onClick={() => setConfirmDelete(false)}
              style={ghostBtnStyle}
            >
              Cancel
            </button>
            <button
              onClick={() => onDelete && onDelete(note.note_id)}
              style={{ ...ghostBtnStyle, color: '#ef4444' }}
            >
              Confirm delete
            </button>
          </>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            title="Delete note"
            style={ghostBtnStyle}
          >
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

export default function NotesDockPanel({
  isOpen,
  onClose,
  sceneId,
  tutorialId,
  domain,
  notes = [],
  onSaveNote,
  onDeleteNote,
  contextAnchor = null,
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset draft text when panel opens for a new scene
  useEffect(() => {
    if (isOpen) {
      setText('');
      setError('');
    }
  }, [isOpen, sceneId]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleSave = useCallback(async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onSaveNote({
        scene_id: sceneId,
        tutorial_id: tutorialId,
        domain,
        text: text.trim(),
        timestamp_sec: contextAnchor?.type === 'timestamp' ? contextAnchor.value : null,
        slide_index: contextAnchor?.type === 'slide' ? contextAnchor.value : null,
        note_type: 'note',
      });
      setText('');
    } catch (e) {
      setError(e?.message || 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  }, [text, sceneId, tutorialId, domain, contextAnchor, onSaveNote]);

  // Resolve context anchor display label
  const anchorLabel = contextAnchor?.type === 'timestamp'
    ? (() => {
        const m = Math.floor(contextAnchor.value / 60);
        const s = String(Math.floor(contextAnchor.value % 60)).padStart(2, '0');
        return `📍 at ${m}:${s}`;
      })()
    : contextAnchor?.type === 'slide'
      ? `Slide ${contextAnchor.value + 1}`
      : null;

  if (!isOpen) return null;

  const regularNotes = notes.filter((n) => n.note_type === 'note');

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 300,
        }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Notes panel"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 360,
          background: '#0f172a',
          color: '#e2e8f0',
          borderLeft: '1px solid #1e293b',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
          zIndex: 301,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          animation: 'slideInRight 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📝</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>My Notes</span>
            {regularNotes.length > 0 && (
              <span style={{
                background: '#3b82f6',
                color: '#fff',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 7px',
              }}>
                {regularNotes.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close notes panel"
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: 20,
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Add note form */}
        <div style={{
          padding: '12px 14px',
          borderBottom: '1px solid #1e293b',
          flexShrink: 0,
        }}>
          {anchorLabel && (
            <div style={{
              fontSize: 11,
              color: '#f59e0b',
              fontWeight: 600,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              {anchorLabel}
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a note…"
            rows={3}
            style={{
              width: '100%',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 7,
              color: '#e2e8f0',
              fontSize: 13,
              padding: '8px 10px',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.5,
              boxSizing: 'border-box',
              outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
            }}
          />
          {error && (
            <div style={{ fontSize: 12, color: '#f87171', marginTop: 4 }}>{error}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              onClick={handleSave}
              disabled={!text.trim() || saving}
              style={{
                background: text.trim() ? '#3b82f6' : '#1e293b',
                color: text.trim() ? '#fff' : '#475569',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: text.trim() ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Saving…' : 'Save (Ctrl+↵)'}
            </button>
          </div>
        </div>

        {/* Notes list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {regularNotes.length === 0 ? (
            <div style={{
              color: '#475569',
              fontSize: 13,
              textAlign: 'center',
              padding: '32px 0',
              lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📝</div>
              No notes for this scene yet.
              <br />
              Add one above.
            </div>
          ) : (
            regularNotes.map((note) => (
              <NoteCard
                key={note.note_id}
                note={note}
                onDelete={onDeleteNote}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

const ghostBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#64748b',
  fontSize: 12,
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
};
